import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { BrainCircuit, Bot, Activity, FileText, Calendar, ChevronRight } from 'lucide-react';
import { useFirebase } from '../context/AuthContext'; // Import the hook

const TrendReportPage = () => {
    const { db } = useFirebase(); // Get the db instance from the context
    const [allReports, setAllReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchAllReports = async () => {
            if (!db) return; // Wait for the db connection to be ready
            try {
                const reportsRef = collection(db, 'trendReports');
                const q = query(reportsRef, orderBy('generatedAt', 'desc'));
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    setError('No trend reports have been generated yet.');
                } else {
                    const reports = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    setAllReports(reports);
                    setSelectedReport(reports[0]);
                }
            } catch (err) {
                setError('Failed to fetch the trend reports. Please try again later.');
                console.error(err);
            }
            setLoading(false);
        };

        fetchAllReports();
    }, [db]);
    
    const ReportContent = ({ report }) => {
        if (!report) return null;

        return (
            <>
                <div className="text-center mb-8">
                    <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-2 flex items-center justify-center">
                        <BrainCircuit size={40} className="mr-4" /> AI Literature Trends
                    </h1>
                    {report.month && (
                         <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg flex items-center justify-center">
                            <Calendar size={18} className="mr-2"/> Report for {report.month}
                        </p>
                    )}
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-800 mb-8">
                     <h2 className="text-xl font-bold text-blue-800 dark:text-blue-300 mb-2 flex items-center"><FileText className="mr-3"/>Executive Summary</h2>
                     <p className="text-md text-blue-700 dark:text-blue-200">{report.overallExecutiveSummary}</p>
                </div>

                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">Clinical Site Analysis</h2>
                    <div className="space-y-6">
                        {report.siteAnalyses && report.siteAnalyses.map((site, index) => (
                            <div key={index} className="bg-gray-100 dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-300 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-blue-800 dark:text-blue-400 mb-4">{site.site}</h3>
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="font-semibold text-gray-700 dark:text-gray-300">This Month's Trend:</h4>
                                        <p className="text-gray-600 dark:text-gray-400">{site.monthlyTrend}</p>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-700 dark:text-gray-300">Comparison to Standard of Care:</h4>
                                        <p className="text-gray-600 dark:text-gray-400">{site.comparisonToStandard}</p>
                                    </div>
                                     <div>
                                        <h4 className="font-semibold text-gray-700 dark:text-gray-300">Emerging Insight:</h4>
                                        <p className="italic text-gray-800 dark:text-gray-200">{site.emergingInsight}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {report.otherTrends && report.otherTrends.length > 0 && (
                     <div className="mt-8">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4 flex items-center"><Activity className="mr-3"/>Other Emerging Trends</h2>
                        <div className="space-y-4">
                             {report.otherTrends.map((trend, index) => (
                                 <div key={index} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
                                    <h4 className="font-bold text-gray-700 dark:text-gray-300">{trend.area}</h4>
                                    <p className="text-gray-600 dark:text-gray-400">{trend.trend}</p>
                                 </div>
                             ))}
                        </div>
                    </div>
                )}

                <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-600 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <p className="flex items-center justify-center"><Bot size={14} className="mr-2"/>This report was generated by an AI analyst based on recently published literature and curated standards of care. It is for educational and informational purposes only.</p>
                </div>
            </>
        );
    };

    if (loading) {
        return <div className="text-center p-8">Loading Trend Reports...</div>;
    }

    if (error) {
        return <div className="text-center p-8 text-red-500">{error}</div>;
    }

    return (
        <div className="flex flex-col md:flex-row gap-8">
            {/* Sidebar for Report Selection */}
            <aside className="md:w-1/4">
                <div className="sticky top-8">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4">Report Archive</h3>
                    <ul className="space-y-2">
                        {allReports.map(report => (
                            <li key={report.id}>
                                <button 
                                    onClick={() => setSelectedReport(report)}
                                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors duration-200 flex items-center justify-between ${selectedReport?.id === report.id ? 'bg-blue-600 text-white font-semibold' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                                >
                                    <span>{report.month || 'Untitled Report'}</span>
                                    {selectedReport?.id === report.id && <ChevronRight size={20} />}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border dark:border-gray-700">
                <ReportContent report={selectedReport} />
            </main>
        </div>
    );
};

export default TrendReportPage;
